import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import styles from './PluginSelector.module.css';
import { PuzzlePiece24Regular } from '@fluentui/react-icons';
import { PublicClientApplication } from "@azure/msal-browser";
import { accessibleIndexGetApi } from "../../api";
import { getMsalConfig } from "../../auth/authUtil";
import { getGraphAccessToken } from "../../auth/getGraphToken";

interface Props {
  environment: any;
  onChange: (selectedPlugins: string[]) => void;
  disabled?: boolean;
}

// 初期インデックス名の解析をヘルパー関数として分離
const parseIndexNames = (indexNamesString: string): string[] => {
  if (!indexNamesString || indexNamesString.trim() === '') return [];
  return indexNamesString
    .split(',')
    .map((name: string) => name.trim())
    .filter((name: string) => name);
};

export const PluginSelector: React.FC<Props> = ({ environment, onChange, disabled }) => {
  const [showOptions, setShowOptions] = useState(false);
  const [pluginSettings, setPluginSettings] = useState<{ [key: string]: boolean }>({});
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [indexNames, setIndexNames] = useState<string[]>(() => parseIndexNames(environment.indexNames));
  const [indexDspNames, setIndexDspNames] = useState<string[]>([]);

  // 表示名のマッピングをuseMemoで最適化
  const displayNames = useMemo(() => {
    const names: { [key: string]: string } = {};
    indexNames.forEach((indexName, index) => {
      names[indexName] = indexDspNames[index] || indexName;
    });
    return names;
  }, [indexNames, indexDspNames]);

  // チェックされた件数をuseMemoで最適化
  const checkedCount = useMemo(() => 
    Object.values(pluginSettings).filter(checked => checked).length,
    [pluginSettings]
  );

  // handleClickOutsideをuseCallbackで最適化
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
      setShowOptions(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    // 初期化時のみpluginSettingsを設定（ボタンクリック時は handleButtonClick で直接処理）
    if (indexNames.length > 0) {
      setPluginSettings(prevSettings => {
        const newSettings = { ...prevSettings };
        indexNames.forEach(indexName => {
          // 既存の設定がない場合のみデフォルト値（false）を設定
          if (!(indexName in newSettings)) {
            newSettings[indexName] = false;
          }
        });
        
        // 不要になったインデックスの設定を削除
        Object.keys(newSettings).forEach(key => {
          if (!indexNames.includes(key)) {
            delete newSettings[key];
          }
        });
        
        return newSettings;
      });
    } else {
      setPluginSettings({});
    }
  }, []); // 初期化時のみ実行

  // disabledが変更されたときにオプションリストを閉じる
  useEffect(() => {
    if (disabled && showOptions) {
      setShowOptions(false);
    }
  }, [disabled]);

  // ボタンクリック時の処理
  const handleButtonClick = async () => {
    if (disabled) return;

    const msalConfig = getMsalConfig(environment);
    const msalInstance = new PublicClientApplication(msalConfig);
    const activeAccount = msalInstance.getActiveAccount();
    const accessToken = await getGraphAccessToken(msalInstance, activeAccount, 'Click PluginSelector');
    
    const accessibleIndex = await accessibleIndexGetApi(accessToken);
    if (accessibleIndex.status === 200) {
      const newIndexNames = Array.isArray(accessibleIndex.indexNames) ? accessibleIndex.indexNames : [];
      const newIndexDspNames = Array.isArray(accessibleIndex.indexDspNames) ? accessibleIndex.indexDspNames : [];
      
      setIndexNames(newIndexNames);
      setIndexDspNames(newIndexDspNames);
      
      // 新しいindexNamesに基づいてpluginSettingsを即座に更新
      setPluginSettings(prevSettings => {
        if (newIndexNames.length === 0) {
          return {}; // indexNamesが空の場合はリセット
        }

        const newSettings: { [key: string]: boolean } = {};
        newIndexNames.forEach(indexName => {
          // 既存の設定がある場合はそれを保持、ない場合はfalse
          newSettings[indexName] = prevSettings[indexName] || false;
        });
        
        // 選択されたプラグインを親コンポーネントに通知
        const selectedPlugins = Object.keys(newSettings).filter(key => newSettings[key]);
        onChange(selectedPlugins);
        
        return newSettings;
      });
      
      // データが存在する場合のみオプションリストを表示
      if (newIndexNames.length > 0) {
        setShowOptions(!showOptions);
      }
    } else {
      setIndexNames([]);
      setIndexDspNames([]);
      setPluginSettings({});
      onChange([]);
    }
  }

  const handleCheckboxChange = (optionValue: string) => {
    // チェックボックスの状態を更新
    const updatedSettings = {
      ...pluginSettings,
      [optionValue]: !pluginSettings[optionValue]
    };
    setPluginSettings(updatedSettings);

    // 選択されたプラグインを検索して親コンポーネントに通知
    const selectedPlugins = Object.keys(updatedSettings).filter(key => updatedSettings[key]);
    onChange(selectedPlugins);
  };

  return (
    <div className={styles.pluginSelector}>
      <button
        onClick={async () => await handleButtonClick()}
        className={`${styles.button} ${disabled ? styles.disabled : ''}`}
        disabled={disabled || indexNames.length === 0}
        title={disabled ? "BingSearch検索がONの場合は使用できません" : indexNames.length === 0 ? "選択可能なRAGインデックスがありません" : "RAG検索インデックス選択"}
      >
        <PuzzlePiece24Regular />
      </button>
      {showOptions && !disabled && indexNames.length > 0 && (
        <div ref={wrapperRef} className={styles.optionsList}>
          <div className={styles.title}>{checkedCount}/{indexNames.length} Enabled</div>
          {Object.keys(displayNames).map((label) => (
            <label key={label} className={`${styles.option}`}>
              <span className={styles.text}>{displayNames[label]}</span>
              <input
                type="checkbox"
                name={label}
                checked={pluginSettings[label]}
                onChange={() => handleCheckboxChange(label)}
              />
            </label>
          ))}
        </div>
      )}
    </div>
  );
};
